import React, { useState } from "react";

import {
    Box,
    Flex,
    Text,
    Icon,
    Divider,
    Stack,
    Tooltip,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalCloseButton,
    ModalBody,
} from "@chakra-ui/react";

import {
    BadgeCheck,
    BadgeInfo,
    BadgeX,
    Download,
    Calendar,
    MapPin,
    Clock,
    IdCard,
    CreditCard,
    Building2,
} from "lucide-react";

import PdfIcon from "assets/svg/pdf.png";
import WordIcon from "assets/svg/word.png";
import ImageIcon from "assets/svg/jpg.png";

import NoDataFound from "components/NoDataGraphics/NoDataFound";
import DocumentViewer from "components/DocumentViewer/DocumentViewer";
import ReusableAvatar from "components/Avatar/ReusableAvatar";
import InfoRow from "components/InfoRow/InfoRow";

import { formatDate } from "utils/dateFormatter";


const DocumentIcon = ({
    type,
}) => {

    switch (type) {

        case "pdf":
            return (
                <img
                    src={PdfIcon}
                    alt="PDF Icon"
                    width={24}
                    height={24}
                />
            );

        case "docx":
            return (
                <img
                    src={WordIcon}
                    alt="Word Icon"
                    width={24}
                    height={24}
                />
            );

        case "jpeg":
        case "jpg":
        case "png":
            return (
                <img
                    src={ImageIcon}
                    alt="Image Icon"
                    width={24}
                    height={24}
                />
            );

        default:
            return null;
    }
};


const ViewAgent = ({
    agent,
}) => {

    const [
        isOpen,
        setIsOpen,
    ] = useState(false);

    const [
        currentDocument,
        setCurrentDocument,
    ] = useState(null);


    if (
        !agent ||
        !agent.agent
    ) {
        return null;
    }


    const agentData =
        agent.agent;

    const user =
        agentData.user ||
        agent.user;


    const {
        agent_number,
        operating_area,
        gps_latitude,
        gps_longitude,
        working_hours,
        payment_method,
        payment_account,
        account_name,
        account_number,
        branch,
        branch_code,
        status,
        status_reason,
        availability_status,
        created_at,
    } = agentData;



    const excludedDocumentTypes = [
        "Chat Image",
        "Payment Proof",
        "Package Evidence Image",
        "Chat Document",
    ];


    const documents = Object.values(

        (
            user?.documents ||
            []
        )

            .filter(
                (doc) =>
                    !excludedDocumentTypes.includes(
                        doc.document_type
                    )
            )

            .reduce(
                (
                    acc,
                    doc
                ) => {

                    const existing =
                        acc[
                            doc.document_type
                        ];

                    if (
                        !existing ||
                        new Date(
                            doc.uploaded_at
                        ) >
                        new Date(
                            existing.uploaded_at
                        )
                    ) {

                        acc[
                            doc.document_type
                        ] = doc;

                    }

                    return acc;

                },
                {}
            )
    );


    const BASE_URL =
        process.env.REACT_APP_API_BASE_URL;



    const handleDocumentClick = (
        doc
    ) => {

        setCurrentDocument({

            url:
                `${BASE_URL.replace(
                    /\/$/,
                    ""
                )}/${doc.file_path.replace(
                    /^\//,
                    ""
                )}`,

            type:
                doc.file_extension,

            title:
                doc.document_type,

            dateUploaded:
                formatDate(
                    doc.uploaded_at
                ),
        });

        setIsOpen(true);
    };


    const closeModal = () => {

        setIsOpen(false);

        setCurrentDocument(
            null
        );
    };


    const openDocumentInNewTab = (
        url
    ) => {

        window.open(
            url,
            "_blank",
            "noopener,noreferrer"
        );
    };


    /* ===================================================== */
    /* PROFILE IMAGE */
    /* ===================================================== */

    const profileImage =
        documents?.find(
            (doc) =>
                doc.document_type ===
                "Profile Image"
        );


    const profileImageUrl =
        profileImage
            ? `${BASE_URL.replace(
                /\/$/,
                ""
            )}/${profileImage.file_path.replace(
                /^\//,
                ""
            )}`
            : null;


    /* ===================================================== */
    /* NAME */
    /* ===================================================== */

    const agentName =
        (
            user?.first_name &&
            user?.last_name
        )
            ? `${user.first_name} ${user.last_name}`
            : user?.username ||
              "Agent Name";


    return (

        <Flex
            direction="column"
            p={8}
            width="100%"
        >

            {/* HEADER */}

            <Flex
                alignItems="center"
            >

                <ReusableAvatar
                    src={
                        profileImageUrl
                    }
                />

                <Box>

                    <Text
                        fontSize="2xl"
                        fontWeight="bold"
                        display="flex"
                        alignItems="center"
                    >

                        {agentName}


                        {status ===
                            "Approved" && (
                            <>
                                <Icon
                                    as={
                                        BadgeCheck
                                    }
                                    size={12}
                                    color="green.500"
                                    stroke="white"
                                    ml={1}
                                    fill="green.500"
                                />

                                <Text
                                    color="green.500"
                                    fontWeight="xs"
                                    fontSize="13px"
                                    ml={1}
                                >
                                    Approved
                                </Text>
                            </>
                        )}

                        {status ===
                            "Active" && (
                            <>
                                <Icon
                                    as={
                                        BadgeCheck
                                    }
                                    size={12}
                                    color="blue.500"
                                    stroke="white"
                                    ml={1}
                                    fill="blue.500"
                                />

                                <Text
                                    color="blue.500"
                                    fontWeight="xs"
                                    fontSize="13px"
                                    ml={1}
                                >
                                    Active
                                </Text>
                            </>
                        )}


                        {status ===
                            "Pending" && (
                            <>
                                <Icon
                                    as={
                                        BadgeInfo
                                    }
                                    size={12}
                                    color="yellow.500"
                                    stroke="white"
                                    ml={1}
                                    fill="yellow.500"
                                />

                                <Text
                                    color="yellow.500"
                                    fontWeight="xs"
                                    fontSize="13px"
                                    ml={1}
                                >
                                    Pending
                                </Text>
                            </>
                        )}


                        {status ===
                            "Rejected" && (
                            <>
                                <Icon
                                    as={
                                        BadgeX
                                    }
                                    size={12}
                                    color="red.500"
                                    stroke="white"
                                    ml={1}
                                    fill="red.500"
                                />

                                <Text
                                    color="red.500"
                                    fontWeight="xs"
                                    fontSize="13px"
                                    ml={1}
                                >
                                    Rejected
                                </Text>
                            </>
                        )}


                        {status ===
                            "Suspended" && (
                            <>
                                <Icon
                                    as={
                                        BadgeX
                                    }
                                    size={12}
                                    color="orange.500"
                                    stroke="white"
                                    ml={1}
                                    fill="orange.500"
                                />

                                <Text
                                    color="orange.500"
                                    fontWeight="xs"
                                    fontSize="13px"
                                    ml={1}
                                >
                                    Suspended
                                </Text>
                            </>
                        )}


                        {status ===
                            "Deactivated" && (
                            <>
                                <Icon
                                    as={
                                        BadgeX
                                    }
                                    size={12}
                                    color="gray.500"
                                    stroke="white"
                                    ml={1}
                                    fill="gray.500"
                                />

                                <Text
                                    color="gray.500"
                                    fontWeight="xs"
                                    fontSize="13px"
                                    ml={1}
                                >
                                    Deactivated
                                </Text>
                            </>
                        )}

                    </Text>


                    <Flex
                        align="center"
                        mt={1}
                    >

                        <Icon
                            as={MapPin}
                            boxSize={3.5}
                            color="gray.500"
                            mr={1}
                        />

                        <Text
                            fontSize="sm"
                            color="gray.500"
                        >
                            {operating_area ||
                                "No operating area specified"}
                        </Text>

                    </Flex>

                </Box>

            </Flex>


            {/* AGENT NUMBER */}

            <Box
                mt={5}
                p={5}
                borderRadius="2xl"
                bgGradient="linear(to-r, blue.900, blue.700)"
                color="white"
                boxShadow="xl"
            >

                <Text
                    fontSize="xs"
                    textTransform="uppercase"
                    opacity={0.8}
                    letterSpacing="1px"
                >
                    Agent Number
                </Text>

                <Text
                    fontSize="3xl"
                    fontWeight="900"
                    mt={1}
                >
                    {agent_number ||
                        "N/A"}
                </Text>

            </Box>


            <Flex
                mt={2}
                alignItems="center"
            >

                <Icon
                    as={Calendar}
                    size={16}
                    color="gray.500"
                    mr={1}
                />

                <Text
                    fontSize="sm"
                    color="gray.500"
                >
                    Date Created:{" "}
                    {formatDate(
                        created_at
                    )}
                </Text>

            </Flex>


            <Divider my={6} />


            {/* AGENT INFO */}

            <Box mb={6}>

                <Text
                    fontSize="md"
                    fontWeight="500"
                    color="gray.500"
                    mb={2}
                >
                    Agent Information
                </Text>

                <Stack spacing={3}>

                    <InfoRow
                        label="Agent Number"
                        value={
                            agent_number
                        }
                        icon={IdCard}
                    />

                    <InfoRow
                        label="Operating Area"
                        value={
                            operating_area
                        }
                        icon={MapPin}
                    />

                    <InfoRow
                        label="Working Hours"
                        value={
                            working_hours
                        }
                        icon={Clock}
                    />

                    <InfoRow
                        label="Availability"
                        value={
                            availability_status
                        }
                        icon={Building2}
                    />

                </Stack>

            </Box>


            <Divider my={6} />


            {/* PAYMENT INFORMATION */}

            <Box mb={6}>

                <Text
                    fontSize="md"
                    fontWeight="500"
                    color="gray.500"
                    mb={2}
                >
                    Payment Information
                </Text>

                <Stack spacing={3}>

                    <InfoRow
                        label="Payment Method"
                        value={
                            payment_method
                        }
                        icon={CreditCard}
                    />

                    <InfoRow
                        label="Payment Account"
                        value={
                            payment_account
                        }
                        icon={CreditCard}
                    />

                    <InfoRow
                        label="Account Name"
                        value={
                            account_name
                        }
                        icon={IdCard}
                    />

                    <InfoRow
                        label="Account Number"
                        value={
                            account_number
                        }
                        icon={CreditCard}
                    />

                    <InfoRow
                        label="Branch"
                        value={
                            branch
                        }
                        icon={Building2}
                    />

                    <InfoRow
                        label="Branch Code"
                        value={
                            branch_code
                        }
                        icon={IdCard}
                    />

                </Stack>

            </Box>


            <Divider my={6} />


            {/* LOCATION */}

            <Box mb={6}>

                <Text
                    fontSize="md"
                    fontWeight="500"
                    color="gray.500"
                    mb={2}
                >
                    Location
                </Text>

                <Stack spacing={3}>

                    <InfoRow
                        label="Operating Area"
                        value={
                            operating_area
                        }
                        icon={MapPin}
                    />

                    <InfoRow
                        label="GPS Latitude"
                        value={
                            gps_latitude
                        }
                        icon={MapPin}
                    />

                    <InfoRow
                        label="GPS Longitude"
                        value={
                            gps_longitude
                        }
                        icon={MapPin}
                    />

                </Stack>

            </Box>


            <Divider my={6} />


            {/* DOCUMENTS */}

            <Box mb={6}>

                <Text
                    fontSize="md"
                    fontWeight="500"
                    color="gray.500"
                    mb={2}
                >
                    Agent Documents
                </Text>


                {documents.length > 0 ? (

                    <Flex
                        wrap="wrap"
                        gap={4}
                    >

                        {documents.map(
                            (doc) => (

                                <Tooltip
                                    label="Click to view"
                                    hasArrow
                                    placement="top"
                                    key={
                                        doc.document_id
                                    }
                                >

                                    <Box
                                        p={2}
                                        border="1px"
                                        borderColor="gray.200"
                                        borderRadius="md"
                                        display="flex"
                                        alignItems="center"
                                        _hover={{
                                            cursor:
                                                "pointer",
                                            bg:
                                                "gray.100",
                                        }}
                                        onClick={() =>
                                            handleDocumentClick(
                                                doc
                                            )
                                        }
                                        w="calc(50% - 8px)"
                                    >

                                        <DocumentIcon
                                            type={
                                                doc.file_extension
                                            }
                                        />

                                        <Box ml={3}>

                                            <Text
                                                fontWeight="bold"
                                                color="gray.600"
                                            >
                                                {
                                                    doc.document_type
                                                }
                                            </Text>

                                            <Text
                                                color="gray.500"
                                                fontSize="sm"
                                            >
                                                Uploaded:{" "}
                                                {formatDate(
                                                    doc.uploaded_at
                                                )}
                                            </Text>

                                        </Box>

                                    </Box>

                                </Tooltip>

                            )
                        )}

                    </Flex>

                ) : (

                    <NoDataFound
                        message="No documents uploaded"
                        width="100px"
                        height="100px"
                        fontSize="sm"
                        fontWeight="bold"
                    />

                )}

            </Box>


            {/* DOCUMENT VIEWER */}

            <Modal
                isOpen={isOpen}
                onClose={closeModal}
            >

                <ModalOverlay />

                <ModalContent
                    maxWidth="500px"
                    maxHeight="80vh"
                    overflowY="auto"
                >

                    <ModalHeader
                        fontSize="16px"
                    >
                        Document Viewer
                    </ModalHeader>

                    <ModalCloseButton />

                    <ModalBody>

                        {currentDocument ? (

                            <>

                                <DocumentViewer
                                    documents={[
                                        currentDocument,
                                    ]}
                                />

                                <Icon
                                    as={Download}
                                    cursor="pointer"
                                    onClick={() =>
                                        openDocumentInNewTab(
                                            currentDocument.url
                                        )
                                    }
                                    mt={4}
                                    boxSize={6}
                                />

                            </>

                        ) : (

                            <NoDataFound
                                message="No document to display."
                            />

                        )}

                    </ModalBody>

                </ModalContent>

            </Modal>

        </Flex>
    );
};


export default ViewAgent;